import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Image as ImageIcon, ExternalLink, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';

const AdminPortfolio = () => {
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Drag and drop & ordering state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    service_category: '',
    project_url: '',
    featured_image: '',
    is_featured: false,
    order_position: 1
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    const { data, error } = await supabase
      .from('portfolio')
      .select('*')
      .order('order_position', { ascending: true });

    if (!error && data) {
      const normalized = data.map((item, idx) => ({
        ...item,
        order_position: typeof item.order_position === 'number' && item.order_position > 0 ? item.order_position : idx + 1
      }));
      setProjects(normalized);
    }
    setLoading(false);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description || '',
      client_name: project.client_name || '',
      service_category: project.service_category || '',
      project_url: project.project_url || '',
      featured_image: project.featured_image || '',
      is_featured: project.is_featured,
      order_position: project.order_position || 1
    });
    setIsEditing(true);
  };

  const handleCreate = () => {
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      client_name: '',
      service_category: '',
      project_url: '',
      featured_image: '',
      is_featured: false,
      order_position: projects.length + 1
    });
    setIsEditing(true);
  };

  // Persist updated positions to Supabase
  const saveNewOrder = async (updatedProjects) => {
    setSavingOrder(true);
    try {
      const updates = updatedProjects.map((project, idx) => {
        return supabase
          .from('portfolio')
          .update({ order_position: idx + 1 })
          .eq('id', project.id);
      });

      const results = await Promise.all(updates);
      const hasError = results.some(r => r.error);
      if (hasError) throw new Error("Impossible de sauvegarder le nouvel ordre");

      toast({
        title: "Ordre mis à jour",
        description: "L'ordre des projets a été actualisé automatiquement."
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err.message
      });
      fetchProjects();
    } finally {
      setSavingOrder(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${index}`);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updated = [...projects];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, moved);

    const reordered = updated.map((item, idx) => ({
      ...item,
      order_position: idx + 1
    }));

    setProjects(reordered);
    setDraggedIndex(null);
    setDragOverIndex(null);

    await saveNewOrder(reordered);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageUpload = async (e) => {
    try {
      setUploading(true);
      const file = e.target.files[0];
      if (!file) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `portfolio-${Math.random()}.${fileExt}`;
      const filePath = `portfolio/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData({ ...formData, featured_image: publicUrl });
      toast({ title: "Image téléversée avec succès" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur lors du téléversement",
        description: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingProject) {
        const { error } = await supabase
          .from('portfolio')
          .update(formData)
          .eq('id', editingProject.id);
        if (error) throw error;
        toast({ title: "Projet mis à jour avec succès" });
      } else {
        const { error } = await supabase
          .from('portfolio')
          .insert([formData]);
        if (error) throw error;
        toast({ title: "Projet créé avec succès" });
      }
      setIsEditing(false);
      fetchProjects();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur lors de l'enregistrement",
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce projet ?")) return;

    try {
      const { error } = await supabase
        .from('portfolio')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast({ title: "Projet supprimé avec succès" });
      fetchProjects();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur lors de la suppression",
        description: error.message
      });
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Portfolio - Admin - Bigadsy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {isEditing ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="flex justify-between items-center mb-6 pb-2 border-b border-slate-200">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{editingProject ? 'Modifier le projet' : 'Nouveau projet'}</h2>
              <p className="text-xs text-slate-500">Renseignez les détails du projet et téléversez une image de présentation</p>
            </div>
            <Button variant="outline" onClick={() => setIsEditing(false)} className="rounded-xl border-slate-200 text-slate-700 hover:bg-slate-100">
              <X className="h-4 w-4 mr-2" /> Annuler
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Titre du projet</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
                    placeholder="Ex: Plateforme E-Commerce Moderne"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nom du client</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={e => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
                    placeholder="Ex: Acme Corp"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Catégorie de prestation</label>
                  <input
                    type="text"
                    value={formData.service_category}
                    onChange={e => setFormData({ ...formData, service_category: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
                    placeholder="Ex: Développement Web"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">URL du projet</label>
                  <input
                    type="url"
                    value={formData.project_url}
                    onChange={e => setFormData({ ...formData, project_url: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
                    placeholder="https://exemple.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label>
                  <textarea
                    rows={4}
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    placeholder="Brève description des travaux réalisés et des résultats..."
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ordre d'affichage (position)</label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={formData.order_position}
                      onChange={e => setFormData({ ...formData, order_position: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-mono font-medium"
                      placeholder="1"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Définit la position d'affichage sur le site public.</p>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2.5 text-slate-800 font-medium text-sm p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100/60 transition-colors w-full h-[46px] mt-1.5">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={e => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                      />
                      <span className="text-xs font-semibold">En vedette sur l'accueil</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Image du projet</label>
                <div className="border-2 border-dashed border-slate-300 bg-slate-50/50 rounded-2xl p-6 text-center hover:bg-slate-100/40 transition-colors">
                  {formData.featured_image ? (
                    <div className="relative">
                      <img src={formData.featured_image} alt="Aperçu" className="h-60 w-full object-cover rounded-xl mb-4 border border-slate-200" />
                      <Button type="button" variant="destructive" size="sm" onClick={() => setFormData({ ...formData, featured_image: '' })} className="absolute top-2 right-2 rounded-lg">Supprimer</Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center py-6">
                      <ImageIcon className="h-12 w-12 text-slate-300 mb-2" />
                      <p className="text-slate-500 text-sm mb-4">Téléversez une capture d'écran ou image de couverture</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)} className="rounded-xl border-slate-200 text-slate-700">Annuler</Button>
              <Button type="submit" disabled={loading} className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 text-white font-semibold rounded-xl px-6 shadow-sm shadow-blue-500/20">
                {loading ? 'Enregistrement...' : 'Enregistrer le projet'}
              </Button>
            </div>
          </form>
        </motion.div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Portfolio</h1>
                {savingOrder && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 animate-pulse">
                    Enregistrement de l'ordre...
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Gérez vos réalisations. Glissez-déposez les lignes pour modifier leur ordre d'affichage.
              </p>
            </div>
            <Button onClick={handleCreate} className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 text-white font-semibold rounded-xl shadow-sm shadow-blue-500/20">
              <Plus className="mr-2 h-4 w-4" /> Ajouter un projet
            </Button>
          </div>

          <div className="bg-white border border-slate-200/90 rounded-2xl shadow-xs overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs font-bold uppercase tracking-wider border-b border-slate-200">
                  <th className="p-4 sm:p-5 w-12 text-center" title="Glisser pour réordonner">
                    <GripVertical className="h-4 w-4 mx-auto text-slate-400" />
                  </th>
                  <th className="p-4 sm:p-5 w-20">Position</th>
                  <th className="p-4 sm:p-5 w-24">Aperçu</th>
                  <th className="p-4 sm:p-5">Projet & Client</th>
                  <th className="p-4 sm:p-5">Catégorie</th>
                  <th className="p-4 sm:p-5">Site web</th>
                  <th className="p-4 sm:p-5">En vedette</th>
                  <th className="p-4 sm:p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {projects.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-400 text-sm">
                      Aucun projet trouvé dans le portfolio
                    </td>
                  </tr>
                ) : (
                  projects.map((project, index) => {
                    const isBeingDragged = draggedIndex === index;
                    const isDragOver = dragOverIndex === index && draggedIndex !== index;

                    return (
                      <tr
                        key={project.id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`text-slate-800 transition-all ${
                          isBeingDragged
                            ? 'opacity-30 bg-blue-50/80 scale-[0.99]'
                            : isDragOver
                              ? 'bg-blue-50/80 ring-2 ring-blue-500/30'
                              : 'hover:bg-slate-50/70'
                        }`}
                      >
                        {/* Drag Handle */}
                        <td className="p-4 text-center cursor-grab active:cursor-grabbing text-slate-400 hover:text-blue-600 select-none w-12">
                          <GripVertical className="h-5 w-5 mx-auto" />
                        </td>

                        {/* Position in small typo */}
                        <td className="p-4">
                          <span className="text-xs font-mono font-bold text-slate-500 bg-slate-100 border border-slate-200/80 px-2 py-0.5 rounded-md">
                            #{project.order_position || index + 1}
                          </span>
                        </td>

                        <td className="p-4 sm:p-5">
                          {project.featured_image ? (
                            <img
                              src={project.featured_image}
                              alt={project.title}
                              className="w-16 h-12 object-cover rounded-lg border border-slate-200 shadow-xs"
                            />
                          ) : (
                            <div className="w-16 h-12 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200 text-slate-400">
                              <ImageIcon className="h-5 w-5 text-slate-400" />
                            </div>
                          )}
                        </td>
                        <td className="p-4 sm:p-5">
                          <div className="font-bold text-slate-900 text-sm">{project.title}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{project.client_name || '—'}</div>
                        </td>
                        <td className="p-4 sm:p-5">
                          <span className="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full uppercase tracking-wider">
                            {project.service_category || 'Général'}
                          </span>
                        </td>
                        <td className="p-4 sm:p-5">
                          {project.project_url ? (
                            <a
                              href={project.project_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-xs font-semibold inline-flex items-center gap-1"
                            >
                              <ExternalLink className="h-3.5 w-3.5" /> Visiter
                            </a>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-4 sm:p-5">
                          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full capitalize ${project.is_featured
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}>
                            {project.is_featured ? 'En vedette' : 'Standard'}
                          </span>
                        </td>
                        <td className="p-4 sm:p-5 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Modifier"
                              onClick={() => handleEdit(project)}
                              className="h-9 w-9 text-blue-600 hover:bg-blue-50 rounded-xl"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Supprimer"
                              onClick={() => handleDelete(project.id)}
                              className="h-9 w-9 text-rose-600 hover:bg-rose-50 rounded-xl"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPortfolio;